import React, { useEffect, useRef, useState } from "react";
import statsDefault from "../data/statsDefault.json";
import useWindowDimensions from "../windowDimensions";

import yearsCompetedIcon from "../stats-icons/yearsCompeted.png";
import firstMedalIcon from "../stats-icons/firstMedal.png";
import medalsWonIcon from "../stats-icons/medalsWon.png";
import recordsBrokenIcon from "../stats-icons/recordsBroken.png";

const iconMap = {
  "World championships won": medalsWonIcon,
  "National championships won": medalsWonIcon,
  "Total records broken": recordsBrokenIcon,
  "First place medals won": firstMedalIcon,
  "Total medals won": medalsWonIcon,
  "Years competing": yearsCompetedIcon,
};

function mergeStats(defaults, fetched) {
  const mergeArray = (defaultArr, fetchedArr = []) => {
    const fetchedMap = Object.fromEntries(fetchedArr.map((s) => [s.label, s.value]));
    return defaultArr.map((s) => ({
      ...s,
      value: s.label in fetchedMap ? fetchedMap[s.label] : s.value,
    }));
  };
  return {
    ...defaults,
    ...fetched,
    allTime: mergeArray(defaults.allTime, fetched.allTime),
    yearStats: mergeArray(defaults.yearStats, fetched.yearStats),
  };
}

function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

function useCountUp(target, duration = 1800) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const hasRun = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasRun.current) {
          hasRun.current = true;
          const start = performance.now();
          const step = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const raw = easeOut(progress) * target;
            const stepped = target > 50 ? Math.round(raw / 5) * 5 : Math.round(raw);
            setDisplay(progress === 1 ? target : stepped);
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  useEffect(() => {
    hasRun.current = false;
    setDisplay(0);
  }, [target]);

  return { ref, display };
}

function StatCard({ value, label, small, prominent, year }) {
  const { ref, display } = useCountUp(Number(value) || 0);
  const icon = iconMap[label];

  return (
    <div
      ref={ref}
      style={{
        textAlign: "center",
        padding: prominent
          ? small ? "20px 16px" : "32px 40px"
          : small ? "16px" : "24px 28px",
        backgroundColor: "#1e1e1e",
        borderRadius: "12px",
        border: prominent ? "2px solid #709425" : "2px solid transparent",
        margin: "8px",
        width: prominent
          ? small ? "calc(50% - 32px)" : "200px"
          : small ? "calc(50% - 32px)" : "160px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {year && (
        <div
          style={{
            fontFamily: "Impact",
            fontSize: small ? "1.1rem" : "1.32rem",
            color: "#709425",
            letterSpacing: "0.1em",
            marginBottom: "4px",
          }}
        >
          {year}
        </div>
      )}
      {icon && (
        <img
          src={icon}
          alt={label}
          style={{
            height: prominent
              ? small ? "72px" : "96px"
              : small ? "64px" : "80px",
            width: "auto",
            maxWidth: "140px",
            marginBottom: "12px",
          }}
        />
      )}
      <div
        style={{
          fontFamily: "Impact",
          fontSize: prominent
            ? small ? "3rem" : "4.5rem"
            : small ? "2.5rem" : "3.5rem",
          color: "#709425",
          lineHeight: 1,
        }}
      >
        {display}+
      </div>
      <div
        style={{
          fontFamily: "Roboto",
          fontSize: small ? "0.75rem" : "0.85rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "white",
          marginTop: "8px",
        }}
      >
        {label}
      </div>
    </div>
  );
}

const TeamStats = () => {
  const [stats, setStats] = useState(statsDefault);
  const { width } = useWindowDimensions();
  const small = width <= 650;

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(
          "https://plantbuilt.s3.us-west-2.amazonaws.com/stats.json",
          { cache: "no-cache" }
        );
        if (response.ok) {
          const data = await response.json();
          setStats(mergeStats(statsDefault, data));
        }
      } catch (e) {
        console.error("Could not load stats from S3, using fallback.", e);
      }
    })();
  }, []);

  return (
    <div
      style={{
        backgroundColor: "#121212",
        minHeight: "100vh",
        padding: small ? "16px 16px" : "24px 32px",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <div
          style={{
            fontFamily: "Impact",
            fontSize: small ? "2.5rem" : "4rem",
            color: "white",
            letterSpacing: "0.05em",
          }}
        >
          PLANTBUILT BY THE NUMBERS
        </div>
        <div
          style={{
            fontFamily: "Roboto",
            color: "#aaaaaa",
            fontSize: "1rem",
            marginTop: "8px",
          }}
        >
          Strength, medals, and records — all plant-powered.
        </div>
      </div>

      {/* All-Time Section — prominent, centered */}
      <div style={{ marginBottom: "32px" }}>
        <div
          style={{
            fontFamily: "Impact",
            fontSize: small ? "1.75rem" : "2.25rem",
            color: "#709425",
            marginBottom: "16px",
            letterSpacing: "0.05em",
          }}
        >
          ALL-TIME
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {stats.allTime.map((stat, idx) => (
            <StatCard key={idx} value={stat.value} label={stat.label} small={small} prominent />
          ))}
        </div>
      </div>

      {/* Current Year Section */}
      <div
        style={{
          borderTop: "3px solid #709425",
          paddingTop: "40px",
        }}
      >
        <div
          style={{
            fontFamily: "Impact",
            fontSize: small ? "1.75rem" : "2.25rem",
            color: "#709425",
            marginBottom: "16px",
            letterSpacing: "0.05em",
          }}
        >
          {stats.currentYear}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-evenly",
          }}
        >
          {stats.yearStats.map((stat, idx) => (
            <StatCard key={idx} value={stat.value} label={stat.label} small={small} />
          ))}
        </div>
      </div>

      {/* Footer note */}
      <div
        style={{
          marginTop: "24px",
          textAlign: "center",
          color: "#666666",
          fontFamily: "Roboto",
          fontSize: "0.8rem",
        }}
      >
        {stats.currentYear} stats updated as competitions are completed.
      </div>
    </div>
  );
};

export default TeamStats;
